/**
 * Archivero financiero. Sub-tab de Finanzas. Lista TODOS los documentos
 * financieros con filtros, búsqueda y tx-linking. Acceso ya gateado por
 * el módulo (Finanzas = admin/finanzas), no se duplica la verificación.
 *
 *   - Filtros por tipo, periodo, empresa y "tiene tx ligada"
 *   - Búsqueda full-text en filename / notas / empresa / tx
 *   - Cada doc muestra meta + chips clickeables por tx ligada
 *   - Upload multi-file con metadata (tipo / periodo / empresa / notas)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Files,
  FolderOpen,
  Search,
  Tag,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";
import { formatDate, formatMxnCents } from "@/lib/format";

interface ArchiveroDoc {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  kind: string;
  period: string | null;
  accountId: string | null;
  companyId: string | null;
  companyName: string | null;
  notes: string | null;
  uploadedById: string;
  createdAt: number;
  linkedTransactions: {
    id: string;
    date: string;
    kind: "ingreso" | "gasto" | "transferencia";
    title: string | null;
    counterparty: string | null;
    totalCents: number;
  }[];
}

interface CompanyOpt {
  id: string;
  name: string;
}

const KIND_GROUP_LABEL: Record<"fiscal" | "contratos" | "corporativo", string> = {
  fiscal: "Fiscal",
  contratos: "Contratos",
  corporativo: "Corporativo",
};

const DOC_KINDS: { value: string; label: string; group: keyof typeof KIND_GROUP_LABEL }[] = [
  { value: "edo_cuenta", label: "Estado de cuenta", group: "fiscal" },
  { value: "factura_emitida", label: "Factura emitida (CFDI)", group: "fiscal" },
  { value: "factura_recibida", label: "Factura recibida (CFDI)", group: "fiscal" },
  { value: "declaracion", label: "Declaración SAT", group: "fiscal" },
  { value: "contrato", label: "Contrato", group: "contratos" },
  { value: "propuesta", label: "Propuesta", group: "contratos" },
  { value: "nda", label: "NDA", group: "contratos" },
  { value: "escritura", label: "Escritura / acta", group: "corporativo" },
  { value: "rfc", label: "Constancia fiscal / RFC", group: "corporativo" },
  { value: "identificacion", label: "Identificación oficial", group: "corporativo" },
  { value: "comprobante_domicilio", label: "Comprobante de domicilio", group: "corporativo" },
  { value: "otro", label: "Otro", group: "corporativo" },
];

function kindLabel(k: string): string {
  return DOC_KINDS.find((d) => d.value === k)?.label ?? k;
}

function kindBadgeColor(k: string): string {
  const group = DOC_KINDS.find((d) => d.value === k)?.group;
  if (group === "fiscal") return "badge-info";
  if (group === "contratos") return "badge-secondary";
  return "badge-ghost";
}

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtPeriod(p: string): string {
  const [y, m] = p.split("-").map(Number);
  if (!y || !m) return p;
  return new Date(y, m - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
}

interface Filters {
  q: string;
  kind: string;
  period: string;
  companyId: string;
  hasTransaction: "all" | "yes" | "no";
}

const EMPTY_FILTERS: Filters = {
  q: "",
  kind: "",
  period: "",
  companyId: "",
  hasTransaction: "all",
};

const MES_LARGO = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

/**
 * Dos vistas:
 *   - "browser": grid de meses del año + sección "Sin periodo". Vista
 *     default cuando entras al archivero. Click en mes → detalle.
 *   - "detail": rica lista del periodo seleccionado (o "sin periodo")
 *     con filtros, búsqueda, tx-linking, upload.
 */
type View = { mode: "browser"; year: number } | { mode: "detail"; period: string | null };

export function ArchiveTab() {
  const [view, setView] = useState<View>({ mode: "browser", year: new Date().getFullYear() });

  if (view.mode === "browser") {
    return (
      <BrowserView
        year={view.year}
        onYearChange={(y) => setView({ mode: "browser", year: y })}
        onSelectPeriod={(period) => setView({ mode: "detail", period })}
      />
    );
  }
  return (
    <DetailView
      lockedPeriod={view.period}
      onBack={() => setView({ mode: "browser", year: new Date().getFullYear() })}
    />
  );
}

// ─── Browser: selector año/mes ─────────────────────────────────────────

function BrowserView({
  year,
  onYearChange,
  onSelectPeriod,
}: {
  year: number;
  onYearChange: (y: number) => void;
  onSelectPeriod: (period: string | null) => void;
}) {
  // Todos los docs del año (incluye los sin periodo — los filtramos abajo).
  const docsQ = useQuery<{ documents: ArchiveroDoc[] }>({
    queryKey: ["archivero", "by-year", year],
    queryFn: () => api.get(`/archivero?year=${year}`),
  });
  const noPeriodQ = useQuery<{ documents: ArchiveroDoc[] }>({
    queryKey: ["archivero", "sin-periodo"],
    queryFn: () => api.get(`/archivero?hasPeriod=0`),
    staleTime: 60_000,
  });

  const monthlyCounts = useMemo(() => {
    const counts = new Map<number, { total: number; byKind: Map<string, number> }>();
    for (let m = 1; m <= 12; m++) counts.set(m, { total: 0, byKind: new Map() });
    for (const d of docsQ.data?.documents ?? []) {
      if (!d.period) continue;
      const m = parseInt(d.period.slice(5, 7), 10);
      const bucket = counts.get(m);
      if (!bucket) continue;
      bucket.total += 1;
      bucket.byKind.set(d.kind, (bucket.byKind.get(d.kind) ?? 0) + 1);
    }
    return counts;
  }, [docsQ.data]);

  const todayY = new Date().getFullYear();
  const todayM = new Date().getMonth() + 1;
  const noPeriodCount = noPeriodQ.data?.documents.length ?? 0;
  const yearTotal = (docsQ.data?.documents ?? []).filter((d) => d.period).length;

  return (
    <div className="space-y-4">
      <div className="card-elevated p-3 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onYearChange(year - 1)}
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Año anterior"
        >
          <ChevronLeft className="size-4" />
        </button>
        <div className="text-lg font-display font-semibold tracking-tight px-2 tabular-nums">
          {year}
        </div>
        <button
          type="button"
          onClick={() => onYearChange(year + 1)}
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Año siguiente"
        >
          <ChevronRight className="size-4" />
        </button>
        {year !== todayY && (
          <button
            type="button"
            onClick={() => onYearChange(todayY)}
            className="btn btn-ghost btn-xs"
          >
            Hoy
          </button>
        )}
        <span className="ml-auto text-xs text-base-content/55 tabular-nums">
          {yearTotal} {yearTotal === 1 ? "documento del año" : "documentos del año"}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {MES_LARGO.map((mes, idx) => {
          const monthNum = idx + 1;
          const period = `${year}-${String(monthNum).padStart(2, "0")}`;
          const bucket = monthlyCounts.get(monthNum)!;
          const isCurrentMonth = year === todayY && monthNum === todayM;
          const isFuture = year > todayY || (year === todayY && monthNum > todayM);
          return (
            <button
              key={period}
              type="button"
              onClick={() => onSelectPeriod(period)}
              className={clsx(
                "card-elevated p-4 text-left transition-all hover:[&]:card-elevated-hover",
                isFuture && "opacity-60",
                isCurrentMonth && "ring-2 ring-primary/40",
              )}
            >
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <span className="font-display font-semibold text-base-content">{mes}</span>
                {bucket.total > 0 && (
                  <span className="badge badge-sm badge-primary tabular-nums">{bucket.total}</span>
                )}
                {bucket.total === 0 && (
                  <span className="text-[10px] text-base-content/40 tabular-nums">—</span>
                )}
              </div>
              {bucket.total > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {[...bucket.byKind.entries()].slice(0, 3).map(([kind, n]) => (
                    <span
                      key={kind}
                      className={clsx("badge badge-xs", kindBadgeColor(kind))}
                      title={kindLabel(kind)}
                    >
                      {kindLabel(kind)} · {n}
                    </span>
                  ))}
                  {bucket.byKind.size > 3 && (
                    <span className="badge badge-xs badge-ghost">+{bucket.byKind.size - 3}</span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-base-content/40">Sin documentos</span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onSelectPeriod(null)}
        className="card-elevated p-4 w-full text-left flex items-center gap-3 hover:[&]:card-elevated-hover"
      >
        <div className="size-10 rounded-md bg-base-200 grid place-items-center shrink-0">
          <FolderOpen className="size-5 text-base-content/65" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold">Sin periodo</div>
          <div className="text-xs text-base-content/55">
            Documentos corporativos, contratos y archivos sin mes específico
          </div>
        </div>
        {noPeriodCount > 0 ? (
          <span className="badge badge-sm badge-ghost tabular-nums">{noPeriodCount}</span>
        ) : (
          <span className="text-[10px] text-base-content/40 tabular-nums">—</span>
        )}
      </button>
    </div>
  );
}

// ─── Detalle: lista rica filtrada por periodo ──────────────────────────

function DetailView({ lockedPeriod, onBack }: { lockedPeriod: string | null; onBack: () => void }) {
  const [filters, setFilters] = useState<Filters>({
    ...EMPTY_FILTERS,
    period: lockedPeriod ?? "",
  });
  const setFilter = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const docsQ = useQuery<{ documents: ArchiveroDoc[] }>({
    queryKey: ["archivero", "detail", lockedPeriod, filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.q) qs.set("q", filters.q);
      if (filters.kind) qs.set("kind", filters.kind);
      if (lockedPeriod) {
        qs.set("period", lockedPeriod);
      } else {
        qs.set("hasPeriod", "0");
      }
      if (filters.companyId) qs.set("companyId", filters.companyId);
      if (filters.hasTransaction === "yes") qs.set("hasTransaction", "1");
      if (filters.hasTransaction === "no") qs.set("hasTransaction", "0");
      return api.get(`/archivero?${qs.toString()}`);
    },
  });

  const companiesQ = useQuery<{ companies: CompanyOpt[] }>({
    queryKey: ["companies"],
    queryFn: () => api.get("/companies"),
    staleTime: 5 * 60_000,
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const docs = docsQ.data?.documents ?? [];

  const title = lockedPeriod ? fmtPeriod(lockedPeriod) : "Sin periodo";

  return (
    <div className="space-y-4">
      <div className="card-elevated p-3 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={onBack} className="btn btn-ghost btn-sm gap-1">
          <ArrowLeft className="size-4" />
          <span className="hidden sm:inline">Volver al año</span>
        </button>
        <div className="font-display font-semibold capitalize text-base-content px-2">{title}</div>
        <span className="ml-auto flex items-center gap-3">
          <span className="text-xs text-base-content/55 tabular-nums">
            {docs.length} {docs.length === 1 ? "documento" : "documentos"}
          </span>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="btn btn-primary btn-sm"
          >
            <Upload className="size-4" /> Subir
          </button>
        </span>
      </div>

      <DetailFilters
        filters={filters}
        setFilter={setFilter}
        companies={companiesQ.data?.companies ?? []}
      />

      {docsQ.isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {!docsQ.isLoading && docs.length === 0 && (
        <DetailEmpty period={lockedPeriod} onUpload={() => setUploadOpen(true)} />
      )}

      {docs.length > 0 && (
        <div className="grid gap-3">
          {docs.map((d) => (
            <DocRow key={d.id} doc={d} />
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadDialog
          defaultPeriod={lockedPeriod ?? ""}
          companies={companiesQ.data?.companies ?? []}
          onClose={() => setUploadOpen(false)}
        />
      )}
    </div>
  );
}

function DetailFilters({
  filters,
  setFilter,
  companies,
}: {
  filters: Filters;
  setFilter: <K extends keyof Filters>(k: K, v: Filters[K]) => void;
  companies: CompanyOpt[];
}) {
  return (
    <div className="card-elevated p-3 flex items-center gap-2 flex-wrap">
      <label className="input input-bordered input-sm flex items-center gap-2 min-w-0 w-72">
        <Search className="size-3.5 text-base-content/40 shrink-0" />
        <input
          type="text"
          className="grow min-w-0"
          placeholder="Buscar en este mes…"
          value={filters.q}
          onChange={(e) => setFilter("q", e.target.value)}
        />
        {filters.q && (
          <button type="button" onClick={() => setFilter("q", "")} className="text-base-content/40">
            <X className="size-3" />
          </button>
        )}
      </label>

      <select
        className="select select-bordered select-sm"
        value={filters.kind}
        onChange={(e) => setFilter("kind", e.target.value)}
      >
        <option value="">Todos los tipos</option>
        {(Object.keys(KIND_GROUP_LABEL) as (keyof typeof KIND_GROUP_LABEL)[]).map((g) => (
          <optgroup key={g} label={KIND_GROUP_LABEL[g]}>
            {DOC_KINDS.filter((k) => k.group === g).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <select
        className="select select-bordered select-sm"
        value={filters.companyId}
        onChange={(e) => setFilter("companyId", e.target.value)}
      >
        <option value="">Cualquier empresa</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      <select
        className="select select-bordered select-sm"
        value={filters.hasTransaction}
        onChange={(e) => setFilter("hasTransaction", e.target.value as Filters["hasTransaction"])}
      >
        <option value="all">Con o sin tx</option>
        <option value="yes">Con tx ligada</option>
        <option value="no">Sin tx ligada</option>
      </select>
    </div>
  );
}

function DetailEmpty({ period, onUpload }: { period: string | null; onUpload: () => void }) {
  return (
    <div className="card-elevated py-16 text-center">
      <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
        <Files className="size-6 text-primary" />
      </div>
      <h3 className="font-display font-semibold text-base-content mb-1">
        {period ? `Sin documentos en ${fmtPeriod(period)}` : "Sin documentos sin periodo"}
      </h3>
      <p className="text-sm text-base-content/60 mb-5">
        Sube CFDI, estados de cuenta o documentos corporativos.
      </p>
      <button type="button" onClick={onUpload} className="btn btn-primary">
        <Upload className="size-4" /> Subir
      </button>
    </div>
  );
}

function DocRow({ doc }: { doc: ArchiveroDoc }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const del = useMutation({
    mutationFn: () => api.del(`/archivero/${doc.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["archivero"] }),
  });

  const download = () => {
    window.open(`/api/archivero/${doc.id}/download`, "_blank");
  };

  const goToTx = (txId: string) => {
    navigate({ to: "/", search: { tab: "transacciones", txId } as never });
  };

  return (
    <div className="card-elevated p-4 flex items-start gap-3">
      <div className="size-10 rounded-md bg-primary/10 grid place-items-center shrink-0">
        <FileText className="size-5 text-primary" />
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{doc.filename}</span>
          <span className={clsx("badge badge-xs", kindBadgeColor(doc.kind))}>
            {kindLabel(doc.kind)}
          </span>
        </div>

        <div className="text-xs text-base-content/55 flex items-center gap-3 flex-wrap tabular-nums">
          <span>{fmtBytes(doc.sizeBytes)}</span>
          <span>· {formatDate(new Date(doc.createdAt * 1000))}</span>
          {doc.period && (
            <span className="inline-flex items-center gap-1 capitalize">
              <Calendar className="size-3" />
              {fmtPeriod(doc.period)}
            </span>
          )}
          {doc.companyName && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="size-3" />
              {doc.companyName}
            </span>
          )}
        </div>

        {doc.notes && <p className="text-xs text-base-content/65 italic">{doc.notes}</p>}

        {doc.linkedTransactions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {doc.linkedTransactions.map((tx) => {
              const sign = tx.kind === "ingreso" ? "+" : tx.kind === "gasto" ? "−" : "";
              const color =
                tx.kind === "ingreso"
                  ? "text-success"
                  : tx.kind === "gasto"
                    ? "text-error"
                    : "text-info";
              return (
                <button
                  key={tx.id}
                  type="button"
                  onClick={() => goToTx(tx.id)}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border border-base-300/60 bg-base-200/40 hover:bg-base-200 hover:border-primary/40 transition-colors"
                  title="Ver transacción en Finanzas → Transacciones"
                >
                  <span className="text-base-content/50 tabular-nums">{tx.date.slice(5)}</span>
                  <span className="truncate max-w-[200px]">
                    {tx.title || tx.counterparty || "—"}
                  </span>
                  <span className={clsx("tabular-nums font-mono", color)}>
                    {sign}
                    {formatMxnCents(tx.totalCents)}
                  </span>
                  <ExternalLink className="size-3 text-base-content/40" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={download}
          className="btn btn-ghost btn-xs btn-square"
          title="Descargar"
          aria-label="Descargar"
        >
          <Download className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`¿Eliminar "${doc.filename}"?`)) del.mutate();
          }}
          disabled={del.isPending}
          className="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error"
          title="Eliminar"
          aria-label="Eliminar"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </div>
  );
}

function UploadDialog({
  companies,
  onClose,
  defaultPeriod = "",
}: {
  companies: CompanyOpt[];
  onClose: () => void;
  defaultPeriod?: string;
}) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [kind, setKind] = useState<string>("otro");
  const [period, setPeriod] = useState<string>(defaultPeriod);
  const [companyId, setCompanyId] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const upload = useMutation({
    mutationFn: async () => {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        if (period) fd.append("period", period);
        if (companyId) fd.append("companyId", companyId);
        if (notes) fd.append("notes", notes);
        await api.post("/archivero", fd);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["archivero"] });
      onClose();
    },
  });

  const totalSize = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-2xl">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display font-semibold text-lg">Subir documento</h3>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={clsx(
              "w-full border-2 border-dashed rounded-lg p-6 text-center transition-colors",
              files.length > 0
                ? "border-primary/50 bg-primary/5"
                : "border-base-300 hover:border-primary/40 hover:bg-base-200/40",
            )}
          >
            <Upload className="size-6 mx-auto text-base-content/40 mb-2" />
            <div className="text-sm">
              {files.length === 0 ? (
                <>
                  <span className="text-primary underline">Selecciona archivos</span>{" "}
                  <span className="text-base-content/60">o arrástralos aquí</span>
                </>
              ) : (
                <span>
                  <strong>{files.length}</strong> {files.length === 1 ? "archivo" : "archivos"} ·{" "}
                  {fmtBytes(totalSize)}
                </span>
              )}
            </div>
            <div className="text-xs text-base-content/55 mt-1">Máximo 100MB por archivo</div>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />

          {files.length > 0 && (
            <ul className="text-xs text-base-content/65 space-y-1 max-h-32 overflow-y-auto">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span className="truncate">{f.name}</span>
                  <span className="tabular-nums shrink-0">{fmtBytes(f.size)}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="form-control">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 inline-flex items-center gap-1">
                <Tag className="size-3" /> Tipo
              </span>
              <select
                className="select select-bordered select-sm"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                {(Object.keys(KIND_GROUP_LABEL) as (keyof typeof KIND_GROUP_LABEL)[]).map((g) => (
                  <optgroup key={g} label={KIND_GROUP_LABEL[g]}>
                    {DOC_KINDS.filter((k) => k.group === g).map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>

            <label className="form-control">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 inline-flex items-center gap-1">
                <Calendar className="size-3" /> Periodo{" "}
                <span className="text-base-content/40">opcional</span>
              </span>
              <input
                type="month"
                className="input input-bordered input-sm"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </label>

            <label className="form-control sm:col-span-2">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 inline-flex items-center gap-1">
                <Building2 className="size-3" /> Empresa{" "}
                <span className="text-base-content/40">opcional</span>
              </span>
              <select
                className="select select-bordered select-sm"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">— Sin empresa —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-control sm:col-span-2">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60">
                Notas <span className="text-base-content/40">opcional</span>
              </span>
              <textarea
                className="textarea textarea-bordered text-sm"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Descripción breve, contexto, etc."
              />
            </label>
          </div>
        </div>

        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => upload.mutate()}
            disabled={files.length === 0 || upload.isPending}
            className="btn btn-primary btn-sm"
          >
            {upload.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Upload className="size-4" />
            )}
            Subir {files.length > 0 ? `(${files.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
